import { AutoBind } from "../rule-engine-core/Common/AutoBind";
import { FlowStepSequence } from "../rule-engine-core/Flow/Structure/FlowStepSequence";
import { FlowStepData } from "../rule-engine-core/Flow/Steps/FlowStepData";
import { StateManager } from "../rule-engine-core/Common/StateManager";
import { DataUtilities } from "../rule-engine-core/Common/DataUtilities";
import { FlowConfiguration, FlowOutputType } from "../rule-engine-core/Flow/FlowConfiguration";
import { IContextForControl } from "../rule-engine-core/Common/IContextForControl";
import { IDraggingElementType } from "./Builder/Common/IDraggingElementType";
import { FlowBuilder } from "./FlowBuilder";
import { FlowConstants } from "../rule-engine-core/Flow/Utilities/FlowConstants";
import { CandidateSequence } from "../rule-engine-core/Flow/Structure/CandidateSequence";
import { IFlowStepSequence } from "../rule-engine-core/Flow/Structure/IFlowStepSequence";
import {
    FlowStepOutputInstructions,
    FlowStepOutputInstructionType,
} from "../rule-engine-core/Flow/Structure/FlowStepOutputInstructions";
import { HTMLShapeGeometry, HTMLUtilities } from "../CommonUI/HTMLUtilities";
import { ReactRenderHook } from "../CommonUI/ReactRenderHook";
import { IContext } from "../rule-engine-core/Common/IContext";
import { DataDefinition } from "../rule-engine-core/Common/Data/DataDefinition";
import { PositionData } from "./Builder/FlowEditContext.PositionData";
import { FlowBuilderPage } from "./Builder/FlowBuilderPage";
import { IFlowDialog } from "./Builder/IFlowDialog";
import { IPosition } from "./Builder/Common/IPosition";
import { IColorElemContext, IColorPickerDialog } from "../CommonUI/BuilderUI/ColorPicker";
import { FlowExtraConnection } from "../rule-engine-core/Flow/Structure/FlowExtraConnection";
import { FlowStepDataRegistration } from "../rule-engine-core/Flow/Utilities/RegisterFlowStepData";
import { BaseFlowStepEditor } from "./Steps/BaseFlowStepEditor";
import { FlowStepEditorRegistration } from "./Utilities/RegisterFlowEditor";
import { AppGlueError } from "../rule-engine-core/AppGlueError";
import { ILine } from "./Structure/DisplayBehavior/ILine";
import { FlowConnection } from "../rule-engine-core/Flow/Structure/FlowConnection";
import { FlowUtilities } from "../rule-engine-core/Flow/Utilities/FlowUtilities";
import { anchorType } from "react-xarrows";
import { Tooltip } from "@mui/material";
import { BranchIcon } from "../CommonUI/Icon/BranchIcon";
import { ReactNode, useState } from "react";
import { FlowSequenceEditRenderer } from "./Builder/FlowSequenceEditRenderer";
import { FlowSequenceCompleteStrategy } from "../rule-engine-core/Flow/Structure/FlowSequenceCompleteStrategy";
import { UILabel } from "../CommonUI/BuildlingBlocks/UILabel";
import { ObserveState } from "../CommonUI/StateManagement/ObserveState";
import { UIButton } from "../CommonUI/BuildlingBlocks/UIButton";
import { FlowMappingLayer } from "./Builder/Components/FlowMappingLayer";
import { AppGlueCoreInit } from "../AppGlueCoreInit";
import { UIToggleButtons } from "../CommonUI/BuildlingBlocks/UIToggleButtons";

interface IDraggingControl {
    destination?: string;
    source: {
        parent?: string;
        current: string;
    };
    pos: IPosition;
}

export class FlowEditContext implements IContext, IColorElemContext {
    static CONNECTIONS_CHANGED: string = "connections";
    static IS_SHOW_EDITOR: string = "isShowEditor";
    static DESIGNER_SCROLL_TOP: string = "designerScrollTop";
    static CONTEXT_CONTROl: string = "contextControl";
    static SHOW_ERROR_BRANCH: string = "isShowErrorBranch";
    static SELECTION_ELEMENT: string = "selectionElement";
    static SELECTED_SEQUENCE_FOR_ERROR: string = "selectedSequenceForError";
    static IS_SHOW_ERROR_BRANCH: string = "isShowErrorBranch";
    static SHOW_ERROR_OR_END_CONNECTIONS: string = "showErrorOrEndConnections";
    static NOTIFICATION: string = "notification";
    static SHOW_STEP_STRATEGY: string = "selectedStepStrategy";
    static SELECTED_FOR_COLOR: string = "selectedForColor";
    static FLOW_NAME: string = "flowName";
    static IS_SHOW_DESCRIPTION: string = "isShowDescription";
    static IS_COLLAPSED: string = "isCollapsed";
    static MODE: string = "mode";
    static DRAGGING_CONTROL: string = "draggingControl";
    static DRAG_OVER_SEQUENCE: string = "dragOverSequence";
    static CLIPBOARD_ELEMENT: string = "clipboardElement";
    static SHOW_SEQUENCE_STRATEGY: string = "showSequenceStrategy";
    private static FLOW_CONTEXT_LISTENER_NAME: string = "FlowContextListenerName__";

    public onFlowSave?: () => void;
    public onFlowCancel?: () => void;

    flowEditor?: FlowBuilder;
    flow: FlowConfiguration;
    flowName?: string;
    viewAPIUrl?: string;
    flowStartDataDefinition: DataDefinition = new DataDefinition();
    mode: FlowBuilderPage = FlowBuilderPage.Flow;
    isShowEditor: boolean = false;
    readonly candidateWidth: number = FlowConstants.PATH_CANDIDATE_WIDTH;
    readonly candidateHeight: number = FlowConstants.PATH_CANDIDATE_HEIGHT;
    readonly candidateDraggingWidth: number = FlowConstants.PATH_DRAGGING_CANDIDATE_WIDTH;
    readonly candidateDraggingHeight: number = FlowConstants.PATH_DRAGGING_CANDIDATE_HEIGHT;
    readonly candidatePadding: string = "0 40px";
    readonly candidateFontSize: number = 12;
    readonly sequenceCandidateShift: number = FlowConstants.SEQUENCE_CANDIDATE_SHIFT;
    readonly pathCandidateShift: number = FlowConstants.PATH_CANDIDATE_SHIFT;
    readonly collapsedWidth: number = FlowConstants.DEFAULT_COLLAPSED_STACK_WIDTH;
    readonly collapsedHeight: number = FlowConstants.DEFAULT_COLLAPSED_STACK_HEIGHT;
    readonly errorCollapsedWidth: number = FlowConstants.COLLAPSED_ERROR_STACK_WIDTH;
    readonly stackWidth: number = FlowConstants.DEFAULT_STACK_WIDTH;
    readonly stackHeight: number = FlowConstants.DEFAULT_STACK_HEIGHT;
    private stepEditors: { [type: string]: BaseFlowStepEditor } = {};

    constructor(flow: FlowConfiguration, flowEditor?: FlowBuilder) {
        this.flow = flow;
        this.flowEditor = flowEditor;

        // check initial positioning
        this.delayPosition();

        // listen to flow
        StateManager.addObserver(flow, (_o: object, val: string[] | undefined) => {
            this.flowChanged(val);
        });

        // setup listeners to all sequences
        this.listenToSequences();

        AppGlueCoreInit.init();
    }

    private _selectedForColor: IColorPickerDialog | undefined = undefined;

    get selectedForColor(): IColorPickerDialog | undefined {
        return this._selectedForColor;
    }

    set selectedForColor(sel: IColorPickerDialog | undefined) {
        this._selectedForColor = sel;
        StateManager.propertyChanged(this, FlowEditContext.SELECTED_FOR_COLOR);
    }

    private _selectedSequenceForError: IFlowEditSequenceDialog | null = null;

    get selectedSequenceForError(): IFlowEditSequenceDialog | null {
        return this._selectedSequenceForError;
    }

    set selectedSequenceForError(s: IFlowEditSequenceDialog | null) {
        this._selectedSequenceForError = s;
        StateManager.propertyChanged(this, FlowEditContext.SELECTED_SEQUENCE_FOR_ERROR);
    }

    private _isShowErrorBranch: boolean = false;

    get isShowErrorBranch(): boolean {
        return this._isShowErrorBranch;
    }

    set isShowErrorBranch(show: boolean) {
        this._isShowErrorBranch = show;
        StateManager.propertyChanged(this, FlowEditContext.IS_SHOW_ERROR_BRANCH);
    }

    private _isShowDescription: boolean = false;

    get isShowDescription(): boolean {
        return this._isShowDescription;
    }

    set isShowDescription(show: boolean) {
        this._isShowDescription = show;
        this.flow.toggleShowDescription(show);
        StateManager.propertyChanged(this, FlowEditContext.IS_SHOW_DESCRIPTION);
    }

    private _selectedSequenceStrategy: IFlowEditSequenceDialog | null = null;

    get selectedSequenceStrategy(): IFlowEditSequenceDialog | null {
        return this._selectedSequenceStrategy;
    }

    set selectedSequenceStrategy(s: IFlowEditSequenceDialog | null) {
        this._selectedSequenceStrategy = s;
        if (s?.sequence) StateManager.changed(s.sequence! as Object);

        StateManager.propertyChanged(this, FlowEditContext.SHOW_SEQUENCE_STRATEGY);
    }

    private _selectedStepStrategy: IFlowEditStepStrategy | null = null;

    get selectedStepStrategy(): IFlowEditStepStrategy | null {
        return this._selectedStepStrategy;
    }

    set selectedStepStrategy(s: IFlowEditStepStrategy | null) {
        this._selectedStepStrategy = s;
        StateManager.propertyChanged(this, FlowEditContext.SHOW_STEP_STRATEGY);
    }

    private _clipboardElement?: FlowStepData | FlowStepSequence;

    get clipboardElement(): FlowStepData | FlowStepSequence | undefined {
        return this._clipboardElement;
    }

    set clipboardElement(elem: FlowStepData | FlowStepSequence | undefined) {
        this._clipboardElement = elem;
        StateManager.propertyChanged(this, FlowEditContext.CLIPBOARD_ELEMENT);
    }

    private _contextControl: IContextForControl | null = null;

    // ------------------------
    // handling flow structure changes
    // ------------------------

    get contextControl(): IContextForControl | null {
        return this._contextControl;
    }

    set contextControl(control: IContextForControl | null) {
        this._contextControl = control;
        StateManager.propertyChanged(this, FlowEditContext.CONTEXT_CONTROl);
    }

    // ------------------------
    // end - handling flow structure changes
    // ------------------------

    // ------------------------
    // force refresh
    // ------------------------

    private _notification?: IFlowDialog;

    get notification() {
        return this._notification;
    }

    set notification(n: IFlowDialog | undefined) {
        this._notification = n;
        StateManager.propertyChanged(this, FlowEditContext.NOTIFICATION);
    }

    private _draggingElemType?: IDraggingElementType;

    // ------------------------
    // end - force refresh
    // ------------------------

    get draggingElemType(): IDraggingElementType | undefined {
        return this._draggingElemType;
    }

    set draggingElemType(type: IDraggingElementType | undefined) {
        this._draggingElemType = type;
    }

    private _selectionElement?: FlowConfiguration | FlowStepData | FlowStepSequence;

    get selectionElement(): FlowConfiguration | FlowStepData | FlowStepSequence | undefined {
        return this._selectionElement;
    }

    private _lastSelectionElement?: FlowConfiguration | FlowStepData | FlowStepSequence;

    get lastSelectionElement(): FlowConfiguration | FlowStepData | FlowStepSequence | undefined {
        return this._lastSelectionElement;
    }

    private _dragOverSequenceId: string | undefined = undefined;

    get dragOverSequenceId(): string | undefined {
        return this._dragOverSequenceId;
    }

    private _draggingControl: IDraggingControl | null = null;

    get draggingControl() {
        return this._draggingControl;
    }

    set draggingControl(con: IDraggingControl | null) {
        this._draggingControl = con;

        StateManager.propertyChanged(this, FlowEditContext.DRAGGING_CONTROL);
    }

    private _showErrorOrEndConnections: FlowStepOutputInstructionType | null = null;

    // showing connections for error or end
    get showErrorOrEndConnections(): FlowStepOutputInstructionType | null {
        return this._showErrorOrEndConnections;
    }

    set showErrorOrEndConnections(isShow: FlowStepOutputInstructionType | null) {
        this._showErrorOrEndConnections = isShow;
        StateManager.propertyChanged(this, FlowEditContext.SHOW_ERROR_OR_END_CONNECTIONS);
    }

    private _isCollapsed: boolean = false;

    get isCollapsed(): boolean {
        return this._isCollapsed;
    }

    set isCollapsed(show: boolean) {
        this._isCollapsed = show;
        this.flow.toggleCollapsed(show);
        StateManager.propertyChanged(this, FlowEditContext.IS_COLLAPSED);
    }

    private _designerScrollTop: number = 0;

    get designerScrollTop(): number {
        return this._designerScrollTop;
    }

    set designerScrollTop(top: number) {
        this._designerScrollTop = top;
        StateManager.propertyChanged(this, FlowEditContext.DESIGNER_SCROLL_TOP);
    }

    set dragOverSequence(sequence: FlowStepSequence | null) {
        let oldSequence: FlowStepSequence | null = null;

        // same sequence no need to update.
        if (sequence && sequence._id === this._dragOverSequenceId) return;

        if (this._dragOverSequenceId) {
            oldSequence = this.flow.find(this._dragOverSequenceId) as FlowStepSequence;
        }

        if (sequence) {
            this._dragOverSequenceId = sequence._id;
            StateManager.propertyChanged(sequence, FlowEditContext.DRAG_OVER_SEQUENCE);
        } else {
            this._dragOverSequenceId = undefined;
        }

        if (oldSequence) {
            StateManager.propertyChanged(oldSequence!, FlowEditContext.DRAG_OVER_SEQUENCE);
        }
    }

    get selection(): string | undefined {
        return this._selectionElement?._id;
    }

    getLine(value: FlowConnection): ILine {
        let targetSequence = this.flow.getCandidateOrRealSequence(value.toId);
        let isHiddenStep = FlowUtilities.IsEndStep(value.fromStep);
        let startId: string | null = null;
        let startAnchor: anchorType = "right";
        let endAnchor: anchorType = [
            "left",
            "top",
            {
                position: "bottom",
                offset: {
                    x: 20,
                    y: -10,
                },
            },
        ];
        let endId = value.toId;

        if (value.isReturn) {
            startId = value.fromId;

            endId = value.toId;
            if (isHiddenStep) {
                endAnchor = {
                    position: "bottom",
                    offset: {
                        x: 20,
                        y: 20,
                    },
                };
            }

            if (!isHiddenStep) {
                if (value.fromSequence.isCollapsed) {
                    endId = value.fromSequence._id;
                    startId = value.fromId;
                }

                endAnchor = {
                    position: "right",
                    offset: {
                        x: -5,
                        y: 10,
                    },
                };
            }

            //            const startSeq = this.editContext.flow.getCandidateOrRealSequence(startId) as FlowStepSequence;

            startAnchor = {
                position: "left",
                offset: {
                    x: 0,
                    y: 10,
                },
            };

            return {
                targetSequence,
                arrow: {
                    start: startId,
                    startAnchor: startAnchor,
                    end: endId,
                    endAnchor: endAnchor,
                    strokeWidth: 2,
                    dashness: true,
                    headSize: 3,
                    color: FlowConstants.DEFAULT_RELATION_LINE_COLOR,

                    //                    avoidRects: this.flow.sequences.map((s) => s._id),
                },
                labels: [],
                value,
            };
        } else {
            if (value.fromSequence.isCollapsed) {
                // no need to split the lines by instruction. just do with squence.
                startId = value.fromSequence._id;
                startAnchor = [
                    {
                        position: "bottom",
                        offset: {
                            x: 20,
                            y: -10,
                        },
                    },
                    {
                        position: "right",
                        offset: {
                            y:
                                Math.ceil(
                                    value.fromSequence.instructions.filter(
                                        (ins) => ins.strategy !== FlowStepOutputInstructionType.BRANCH,
                                    ).length / 2,
                                ) * 16,
                            x: -12,
                        },
                    },
                    "top",
                    "left",
                ];
            } else {
                startId = value.fromId;
            }

            if (isHiddenStep) {
                startAnchor = "bottom";
            }

            if (targetSequence instanceof CandidateSequence && !isHiddenStep) {
                endAnchor = "left";
            }

            const tooltip = value.fromInstruction.pathName;
            const label =
                tooltip.length > FlowConstants.MAX_CHARS_STEP_PATH_LABEL
                    ? `${tooltip.slice(0, FlowConstants.MAX_CHARS_STEP_PATH_LABEL)}...`
                    : tooltip;

            return {
                targetSequence,
                arrow: {
                    start: startId,
                    startAnchor: startAnchor,
                    end: endId,
                    endAnchor: endAnchor,
                    strokeWidth: 2,
                    headSize: 3,
                    color: FlowConstants.DEFAULT_RELATION_LINE_COLOR,
                    //avoidRects: this.flow.sequences.map((s) => s._id),
                },
                labels: [
                    {
                        label: (
                            <Tooltip title={tooltip}>
                                <span style={{ display: "flex", alignItems: "center" }}>
                                    {isHiddenStep && <BranchIcon />} {label}
                                </span>
                            </Tooltip>
                        ),
                        value,
                        isFlowConnection: isHiddenStep,
                    },
                ],
                value,
            };
        }
    }

    getLines() {
        let connections = this.flow.getConnections();

        connections = connections.filter((c) => {
            // hide the line while dragging
            if (c.fromStep._id === this.draggingControl?.source.current) {
                return false;
            }

            return true;
        });

        return connections.reduce((lines: ILine[], value: FlowConnection) => {
            let line = this.getLine(value);

            let isSet = false;
            if (!value.isReturn) {
                for (let l of lines) {
                    // combining the lines by start sequence and end sequence.
                    if (l.arrow.end === line.arrow.end && l.value.fromSequence._id === line.value.fromSequence._id) {
                        l.labels = [...l.labels, ...line.labels];
                        l.arrow.start = l.value.fromSequence._id;
                        isSet = true;
                        break;
                    }
                }
            }

            if (!isSet) {
                lines.push(line);
            }

            return lines;
        }, []);
    }

    listenToSequences() {
        for (let s of this.flow.allSequences) {
            StateManager.removeObserverByName(s, FlowEditContext.FLOW_CONTEXT_LISTENER_NAME);
            StateManager.addObserver(
                s,
                (o: object, val: string[] | undefined) => {
                    this.flowSequenceChanged(o as FlowStepSequence, val);
                },
                undefined,
                FlowEditContext.FLOW_CONTEXT_LISTENER_NAME,
            );
        }
    }

    @AutoBind
    flowChanged(properties: string[] | undefined) {
        if (properties && properties.length !== 0 && properties?.indexOf(FlowConfiguration.CANDIDATE_SEQUENCES) !== -1)
            StateManager.propertyChanged(this, FlowConfiguration.CANDIDATE_SEQUENCES);

        if (properties && properties.length !== 0 && properties?.indexOf(FlowConfiguration.SEQUENCES) !== -1)
            StateManager.propertyChanged(this, FlowConfiguration.SEQUENCES);

        if (properties && properties.indexOf(FlowConfiguration.ADD_SEQUENCE) !== -1) {
            this.listenToSequences();
        }
        if (properties && properties.indexOf(FlowConfiguration.REMOVE_SEQUENCE) !== -1) {
            this.listenToSequences();
        }

        if (properties && properties.indexOf(FlowConfiguration.OUTPUT_STRATEGY) !== -1) {
            StateManager.propertyChanged(this, FlowConfiguration.OUTPUT_STRATEGY);
        }

        if (
            properties === undefined ||
            properties.length === 0 ||
            properties?.indexOf(FlowConfiguration.SEQUENCES) !== -1 ||
            properties?.indexOf(FlowConfiguration.CANDIDATE_SEQUENCES) !== -1 ||
            properties?.indexOf(FlowConfiguration.REMOVE_SEQUENCE) !== -1 ||
            properties?.indexOf(FlowConfiguration.ADD_SEQUENCE) !== -1
        )
            this.delayPosition();
    }

    @AutoBind
    flowSequenceChanged(_sequence: FlowStepSequence, properties: string[] | undefined) {
        if (
            properties === undefined ||
            properties?.indexOf(FlowStepSequence.STEPS_CHANGED) !== -1 ||
            properties?.indexOf(FlowStepSequence.MOVED) !== -1 ||
            properties?.indexOf(FlowStepSequence.COLLAPSING) !== -1 ||
            properties?.indexOf(FlowStepSequence.EXPANDING) !== -1 ||
            properties?.indexOf(FlowStepSequence.IS_COLLAPSED) !== -1 ||
            properties?.indexOf(FlowStepSequence.DRAG_END) !== -1 ||
            properties?.indexOf(FlowStepSequence.BRANCHES_CHANGED) !== -1
        ) {
            this.delayPosition();
        }

        if (properties !== undefined && properties?.indexOf(FlowStepSequence.STEPS_CHANGED) !== -1) {
            this.flow.invalidateNodes();
        }

        if (properties === undefined || properties?.indexOf(FlowStepSequence.STRATEGY) !== -1) {
            this.syncCandidates();
            this.delayPosition(true);
        }

        if (properties?.indexOf(FlowStepSequence.DRAG_OVER) !== -1)
            StateManager.propertyChanged(this, FlowStepSequence.DRAG_OVER);

        if (properties?.indexOf(FlowStepSequence.STRATEGY) !== -1)
            StateManager.propertyChanged(this, FlowStepSequence.STRATEGY);

        if (properties?.indexOf(FlowStepSequence.DRAG_END) !== -1) this.refreshLines();
    }

    getOrCreateStepEditor(type: string, data?: FlowStepData): BaseFlowStepEditor {
        if (data) {
            let editor = this.stepEditors[data?._id];
            if (editor) return editor;
        }

        let reg = FlowStepEditorRegistration.getByTypeName(type);

        if (!reg) throw new AppGlueError("could not find registratio for type " + type);

        return reg.create(this, data);
    }

    getStepEditor(id: string): BaseFlowStepEditor | undefined {
        let step = this.flow.find(id) as FlowStepData;

        if (!step) throw new AppGlueError("flow step not found " + id);
        return this.getOrCreateStepEditor(step.__type!, step);
    }

    registerStepEditor(_id: string, control: BaseFlowStepEditor) {
        this.stepEditors[_id] = control;
    }

    // eslint-disable-next-line
    onStepMoved(fromSequence: IFlowStepSequence, toSequence: IFlowStepSequence, step: FlowStepData): void {
        // if to sequence is collapsed, set it expanded and select added step.
        if (toSequence instanceof FlowStepSequence && toSequence.isCollapsed) {
            toSequence.isCollapsed = false;
            this.setSelection(step);
        }

        this.delayPosition();
    }

    renderEditUI(
        editContext: FlowEditContext,
        elem: FlowConfiguration | FlowStepData | FlowStepSequence | undefined,
    ): ReactNode {
        if (!elem) return null;

        if (elem instanceof FlowConfiguration) {
            return <RenderFlowSettingsEditor flowConfig={elem} />;
        } else if (elem instanceof FlowStepSequence) {
            return new FlowSequenceEditRenderer(elem as FlowStepSequence).renderEditUI();
        } else {
            let stepui = this.getOrCreateStepEditor((elem as FlowStepData).__type!, elem as FlowStepData);
            return stepui.renderEditUI();
        }
    }

    getSequenceWidth(sequence: FlowStepSequence) {
        return this.getSequenceMainSize(sequence).width || 0;
    }

    getSequenceY(sequence: FlowStepSequence) {
        // we need to get the updated position of end / error using geometry as we are not setting the position data for them in positioned logic.
        if (sequence.isEnd || sequence.isError) {
            return (
                (this.geometryForElementById(sequence._id)?.top || 0) +
                (this.designerScrollTop || 0) -
                (this.designerGeometry()?.top || 0)
            );
        }

        return sequence.y;
    }

    geometryForElementById(id: string): HTMLShapeGeometry | undefined {
        return HTMLUtilities.getGeometry(id, FlowConstants.DESIGN_SURFACE_ID);
    }

    designerGeometry(): HTMLShapeGeometry | undefined {
        return HTMLUtilities.getGeometry(FlowConstants.DESIGN_SURFACE_ID);
    }

    refreshSequences(): void {
        StateManager.propertyChanged(this, FlowConfiguration.CANDIDATE_SEQUENCES);
        StateManager.propertyChanged(this.flow, FlowConfiguration.SEQUENCES);
        // it calls refresh lines
        this.refreshLines();
    }

    refreshLines(): void {
        StateManager.propertyChanged(this, FlowEditContext.CONNECTIONS_CHANGED);
    }

    getCandidateSequences(): IFlowStepSequence[] {
        return this.flow.candidateSequences;
    }

    getSequenceByXY(x: number, y: number): FlowStepSequence | null {
        for (let s of [...this.flow.allSequences, this.flow.endSequence, this.flow.errorSequence]) {
            // if (x > s.x && x < s.x + s.width && y > s.y && y < s.y + s.height)
            //     return s;

            let sequenceShape = HTMLUtilities.getGeometry(s._id);
            if (sequenceShape) {
                if (x > s.x && x < s.x + sequenceShape.width && y > s.y && y < s.y + sequenceShape.height) return s;
            }
        }
        return null;
    }

    purgeCandidateSequences(): void {
        this.syncCandidates();
        this.positionCandidateSequences(false);
    }

    getErrorSeqeuenceMainSize(sequence: FlowStepSequence) {
        if (sequence.isCollapsed) {
            return {
                width: this.errorCollapsedWidth,
                height: this.collapsedHeight,
                fontSize: 16,
                title: {
                    color: FlowConstants.ERROR_COLLAPSED_TITLE_COLOR,
                },
            };
        }

        return {
            width: FlowConstants.DEFAULT_STACK_WIDTH,
            height: sequence.steps.length === 0 ? 100 : undefined,
            fontSize: 16,
            title: {
                color: FlowConstants.ERROR_TITLE_COLOR,
            },
        };
    }

    getEndSequenceMainSize(sequence: FlowStepSequence) {
        if (sequence.isCollapsed) {
            return {
                width: this.errorCollapsedWidth,
                height: this.collapsedHeight,
            };
        }

        return {
            width: FlowConstants.DEFAULT_STACK_WIDTH,
            height: sequence.steps.length === 0 ? 100 : undefined,
        };
    }

    getPrimaryMainSize(sequence: FlowStepSequence) {
        if (sequence.isCollapsed) {
            return {
                width: this.collapsedWidth,
                height: this.collapsedHeight,
            };
        }

        return {
            width: this.stackWidth,
            height: sequence.steps.length === 0 ? this.stackHeight : undefined,
        };
    }

    getSequenceMainSize(sequence: FlowStepSequence) {
        // error stack common styles
        if (sequence.isError) return this.getErrorSeqeuenceMainSize(sequence);
        // end stack common styles
        if (sequence.isEnd) return this.getEndSequenceMainSize(sequence);
        //primary stack common styles
        if (sequence.isPrimary) return this.getPrimaryMainSize(sequence);

        if (sequence.isCollapsed) {
            return {
                width: this.collapsedWidth,
                height: this.collapsedHeight,
            };
        }

        return {
            width: this.stackWidth,
            height: sequence.steps.length === 0 ? this.stackHeight : undefined,
        };
    }

    // eslint-disable-next-line

    //    @LogTime()
    positionCandidateSequences(requirePurge: boolean = true): boolean {
        let wasRepositioned = false;

        if (requirePurge) {
            wasRepositioned = this.syncCandidates();
        }

        let seqList = this.flow.sequences;

        // lookup position and set for instructions by sequence.
        for (let sequence of seqList) {
            this.setCandidateDesiredPositionForSeqeuence(sequence);
        }

        // lookup position and set for all instructions
        // for (let inst of this.flow.getAlternateOutputPaths()) {
        // set candidate desired position by view mode.
        // this.displayBehavior.setCandidateDesiredPositionForStepStrategy(inst, this.flow);
        // let pos = HTMLUtilities.getGeometry(inst.getElementId(), FlowConstants.DESIGN_SURFACE_ID);

        // if (pos) {

        //     let insty = pos.top + 18 - (candidateStyles.height / 2);
        //     let instx= pos.left + FlowConstants.PATH_CANDIDATE_SHIFT;

        //     // when it's step for sequence strategy
        //     if (inst.stepId.endsWith('_end')) {
        //         insty = insty - 20;
        //         // let seq = this.flow.findSequenceByStepId(inst.stepId)

        //         // if not collapsed, set candidate for sequence strategy to bottom of sequence
        //         // otherwise, it's set side of sequence like as other candidate for normal step pathes.
        //         // if (!this.displayBehavior.isCollapsed(seq!)) {
        //         //     // for hidden step instruction only applied when expanded;
        //         //     insty = insty + FlowConstants.SEQUENCE_STRATEGY_Y_OFFSET;
        //         //     instx = instx - FlowConstants.SEQUENCE_STRATEGY_X_OFFSET;
        //         // }
        //     }
        //   //  inst.postionHistory.push({x: instx, y: insty, from: 'poll'});

        //     inst.candidateStackY = insty;
        //     inst.candidateStackX = instx;
        // }

        // }

        let workingSet: PositionData[] = [];

        for (let realS of this.flow.candidateSequences) {
            let geo = HTMLUtilities.getGeometry(realS.getElementId());

            if (!geo) {
                // todo: need to get width and height from display behavior
                geo = new HTMLShapeGeometry(
                    realS.desiredY,
                    realS.desiredX,
                    FlowConstants.PATH_CANDIDATE_HEIGHT,
                    FlowConstants.PATH_CANDIDATE_WIDTH,
                );
            } else {
                geo.left = realS.desiredX;
                geo.top = realS.desiredY;
            }

            geo.bottomPadding = 5;

            workingSet.push(new PositionData(geo, false, true, realS, realS.x, realS.y));
        }

        // add real sequences to this
        for (let realS of seqList) {
            let geo = HTMLUtilities.getGeometry(realS.getElementId());

            if (!geo) {
                // todo: need to get width and height from display behavior
                geo = new HTMLShapeGeometry(
                    realS.x,
                    this.getSequenceY(realS),
                    this.getSequenceMainSize(realS).width,
                    this.getSequenceMainSize(realS).height || FlowConstants.DEFAULT_STACK_HEIGHT,
                );
            }

            geo.topPadding = FlowConstants.TITLE_AREA_HEIGHT;
            geo.bottomPadding = this.getSequenceExtraSize(realS).height || 0;
            geo.rightPadding = this.getSequenceExtraSize(realS).width;

            geo.top = realS.desiredY;
            geo.left = realS.desiredX;

            workingSet.push(new PositionData(geo, false, false, realS, realS.x, this.getSequenceY(realS)));

            if (geo) {
                realS.height = geo.height;
            }
        }

        for (let candS of workingSet) {
            candS.sequence.reset();
        }

        // sort working set
        workingSet = workingSet.sort((a: PositionData, b: PositionData) => {
            // sort real above candidates
            if (a.isCandidate !== b.isCandidate) {
                if (a.isCandidate) return 1;
                else return -1;
            }

            if (a.shape.top + a.shape.left > b.shape.top + b.shape.left) {
                return 1;
            } else {
                return -1;
            }
        });

        for (let positionThis of workingSet) {
            // find any that are in vertical range
            let consider: PositionData[] = [];

            // console.log('positioning', positionThis)

            for (let otherPostion of workingSet) {
                if (!otherPostion.positioned) continue;

                if (positionThis.inRange(otherPostion)) consider.push(otherPostion);
            }

            // position so its missed
            PositionData.positionOneSequence(positionThis, consider);
        }

        if (wasRepositioned) return true;

        for (let o of workingSet) {
            if (o.initialX !== o.sequence.x || o.initialY !== o.sequence.y) {
                return true;
            }
        }

        return false;
    }

    isDragOver(id: string) {
        return this.dragOverSequenceId === id;
    }

    canCombine(combine: IFlowStepSequence, withSequence: IFlowStepSequence): boolean {
        // todo: fix this

        // if (combine instanceof CandidateSequence) {
        //
        //     // ui should not allow this
        //     if (step.sequence?._id === withSequence._id)
        //         return false;
        // }

        if (combine instanceof CandidateSequence) {
            let sequence = this.flow.findSequenceByStepId(combine.instruction.stepId);

            if (sequence) {
                // ui should not allow this
                if (sequence?._id === withSequence._id) return false;
                return this.flow.canLink(sequence, withSequence as FlowStepSequence);
            }
        } else if (combine instanceof FlowStepSequence) {
            return this.flow.canLink(combine, withSequence as FlowStepSequence);
        }

        return true;
    }

    combineSequences(combine: IFlowStepSequence, withSequence: IFlowStepSequence): void {
        // ui should not allow this
        if (!this.canCombine(combine, withSequence)) {
            this.notification = {
                message: "You can not link to this sequence.",
                onSuccess: () => {},
            };
            return;
        }
        if (withSequence instanceof CandidateSequence) {
            throw new Error("Can not combine / link the candidate sequences");
        } else if (combine instanceof CandidateSequence && combine.instruction) {
            // linking

            let step = this.flow.find(combine.instruction.stepId) as FlowStepData;

            let inst = step!.findOutputInstruction(combine.instruction.pathName);

            if (!inst) throw new Error("instruction not found");

            inst.connectedSequenceId = withSequence._id;

            StateManager.changed(inst);

            // remove candidate sequence
            this.flow.candidateSequences = this.flow.candidateSequences.filter((c) => c._id !== combine._id);
            this.refreshSequences();
        } else if (combine instanceof FlowStepSequence) {
            // combining

            for (let s of combine.steps) {
                this.flow.moveToSequence(s, combine, withSequence as FlowStepSequence);
            }

            for (let seq of this.flow.allSequences) {
                if (seq._id === combine._id) continue;
                for (let s of seq.steps) {
                    for (let ins of s.getOutcomeInstructions()) {
                        if (
                            ins.connectedSequenceId === combine._id &&
                            ins.strategy === FlowStepOutputInstructionType.BRANCH
                        ) {
                            ins.connectedSequenceId = withSequence._id;
                        }
                    }
                }
            }

            // delete combine
            let idx = this.flow.allSequences.indexOf(combine);
            this.deleteSequence(idx);
            this.refreshSequences();
        }
    }

    @AutoBind
    onCopy(selectedId?: string) {
        let elem = this._lastSelectionElement;
        if (selectedId) {
            elem = this.flow.find(selectedId);
            this.contextControl = null;
        }
        if (!elem) return;

        if (elem instanceof FlowConfiguration) {
            // do nothing
        } else if (elem instanceof FlowStepSequence && !(elem as FlowStepSequence).canCopy) {
            this.notification = {
                message: "You can not copy this sequence",
                onSuccess: () => {},
            };
        } else {
            this.clipboardElement = this.cloneFlowElement(elem) as FlowStepData;
        }
    }

    @AutoBind
    onCut(selectedId?: string) {
        let elem = this._lastSelectionElement;
        if (selectedId) {
            elem = this.flow.find(selectedId);
            this.contextControl = null;
        }
        if (!elem) return;

        if (elem instanceof FlowStepSequence) {
            if (!(elem as FlowStepSequence).canCopy) {
                this.notification = {
                    message: "You can not copy this sequence",
                    onSuccess: () => {},
                };
                return;
            }

            let idx = this.flow.allSequences.indexOf(elem as FlowStepSequence);
            if (idx > 0) {
                if ((elem as FlowStepSequence).steps.length > 0) {
                    this.notification = {
                        message: "This sequence has the steps. Do you want to cut it really?",
                        onSuccess: () => {
                            this.clipboardElement = this.cloneFlowElement(elem as FlowStepSequence);
                            this.deleteSequence(idx);
                        },
                        onCancel: () => {},
                    };
                } else {
                    this.deleteSequence(idx);
                }
            } else {
                this.notification = {
                    message: "Primary Stack can not be cut. But it's copied to clipboarded",
                    onSuccess: () => {},
                };
            }
        } else {
            this.clipboardElement = this.cloneFlowElement(elem as FlowStepData);
            this.flow.remove(elem as FlowStepData);
        }
    }

    @AutoBind
    deleteSequence(idx: number) {
        this.flow.deleteSequenceByIndex(idx);
        StateManager.propertyChanged(this.flow, FlowConfiguration.SEQUENCES);
        this.purgeCandidateSequences();
    }

    @AutoBind
    onDelete(selectedId?: string) {
        let elem = this._lastSelectionElement;
        if (selectedId) {
            elem = this.flow.find(selectedId);
            this.contextControl = null;
        }
        if (!elem) return;

        if (elem instanceof FlowStepSequence) {
            if (!(elem as FlowStepSequence).canDelete) {
                this.notification = {
                    message: "You can not delete this sequence",
                    onSuccess: () => {},
                };
                return;
            }
            let idx = this.flow.allSequences.indexOf(elem as FlowStepSequence);
            if (idx > 0) {
                if ((elem as FlowStepSequence).steps.length > 0) {
                    this.notification = {
                        message: "This sequence has the steps. If you delete this, you will lost all data. it's okay?",
                        onSuccess: () => {
                            this.deleteSequence(idx);
                        },
                        onCancel: () => {},
                    };
                } else {
                    this.deleteSequence(idx);
                }
            } else {
                this.notification = {
                    message: "Primary Stack can not be deleted.",
                    onSuccess: () => {},
                };
            }
        } else {
            this.flow.remove(elem as FlowStepData);
            this.purgeCandidateSequences();
        }
    }

    @AutoBind
    onPaste() {
        if (!this.clipboardElement || (!this.selectionElement && !(this.clipboardElement instanceof FlowStepSequence)))
            return;

        if (this.clipboardElement instanceof FlowStepSequence) {
            let newSeq = this.cloneFlowElement(this.clipboardElement) as FlowStepSequence;
            newSeq._id = DataUtilities.generateUniqueId();

            newSeq.desiredY += 20;
            newSeq.desiredX += 20;
            newSeq.x += 20;
            newSeq.y += 20;
            this.flow.addSequence(newSeq);
        } else if (
            !(this.clipboardElement instanceof FlowStepSequence) &&
            !(this.selectionElement instanceof FlowStepSequence) &&
            this.selectionElement
        ) {
            for (let s of this.flow.allSequences) {
                let idx = s.steps.indexOf(this.selectionElement as FlowStepData);
                if (idx >= 0) {
                    let elem = this.cloneFlowElement(this.clipboardElement) as FlowStepData;
                    elem._id = DataUtilities.generateUniqueId();
                    this.flow.add(elem, s, idx);
                    break;
                }
            }
        }
    }

    setSelection(selection: FlowConfiguration | FlowStepData | FlowStepSequence) {
        this._selectionElement = selection;
        StateManager.changed(selection);
        if (this._lastSelectionElement) {
            StateManager.changed(this._lastSelectionElement);
        }
        this._lastSelectionElement = selection;
        StateManager.propertyChanged(this, FlowEditContext.SELECTION_ELEMENT);
        this.showEditLayer();
    }

    clearSelection() {
        this._selectionElement = undefined;
        StateManager.propertyChanged(this, FlowEditContext.SELECTION_ELEMENT);
        this.hideEditLayer();
    }

    addStepToCandidate(step: FlowStepData, candidate: CandidateSequence) {
        let s = candidate.createSequence();

        let instruction = Reflect.get(candidate, "instruction") as FlowStepOutputInstructions;
        if (instruction) {
            // hook the paths together
            if (instruction.pathName && instruction.stepId) {
                instruction.connectedSequenceId = s._id;
            }
        }
        this.flow.addSequence(s);
        s.strategy = FlowSequenceCompleteStrategy.END_FLOW;
        s.addStep(step);
    }

    moveStepToCandidate(step: FlowStepData, sequence: FlowStepSequence, candidate: CandidateSequence) {
        if (!this.flow.canMoveToSequence(step, candidate)) {
            throw new Error("You can not move this step");
        }
        sequence.removeStep(step);
        this.addStepToCandidate(step, candidate);
    }

    @AutoBind
    toggleEditLayer() {
        this.isShowEditor = !this.isShowEditor;
        StateManager.propertyChanged(this, FlowEditContext.IS_SHOW_EDITOR);
    }

    @AutoBind
    hideEditLayer() {
        this.isShowEditor = false;
        StateManager.propertyChanged(this, FlowEditContext.IS_SHOW_EDITOR);
    }

    @AutoBind
    showEditLayer() {
        this.isShowEditor = true;
        StateManager.propertyChanged(this, FlowEditContext.IS_SHOW_EDITOR);
    }

    refresh() {
        if (this.flowEditor) {
            StateManager.changed(this);
            StateManager.changed(this.flow);
        }
    }

    @AutoBind
    setSelectedStepStrategy(elem: HTMLElement, step: FlowStepData) {
        let elemGeo = HTMLUtilities.getGeometryFromElement(elem, FlowConstants.DESIGN_SURFACE_ID);
        this.selectedStepStrategy = {
            position: {
                x: elemGeo?.left || 0,
                y: elemGeo?.top || 0,
            },
            step,
        };
    }

    @AutoBind
    setSelectedSequenceStrategy(elem: HTMLElement, sequence: FlowStepSequence) {
        let elemGeo = HTMLUtilities.getGeometryFromElement(elem, FlowConstants.DESIGN_SURFACE_ID);
        this.selectedSequenceStrategy = {
            position: {
                x: elemGeo?.left || 0,
                y: elemGeo?.top || 0,
            },
            sequence,
        };
    }

    getEndFlowConnections(): FlowExtraConnection[] {
        let conn: FlowExtraConnection[] = [];
        // showErrorOrEndConnections is set END FLOW or end stack is expanded.
        if (
            this.showErrorOrEndConnections === FlowStepOutputInstructionType.END_FLOW ||
            !this.flow.endSequence.isCollapsed ||
            this.isShowErrorBranch
        ) {
            for (let s of this.flow.allSequences) {
                for (let step of [...s.steps, s.endStep]) {
                    if (
                        step.sequence?._id === FlowConstants.ERROR_SEQUENCE ||
                        step.sequence?._id === FlowConstants.END_SEQUENCE
                    ) {
                        //console.log("skipping self connections");
                        continue;
                    }
                    step.getOutcomeInstructions().forEach((ins: FlowStepOutputInstructions) => {
                        if (ins.strategy === FlowStepOutputInstructionType.END_FLOW) {
                            let fromStep = this.flow.find(ins.stepId);
                            if (fromStep) {
                                conn.push(
                                    new FlowExtraConnection(
                                        ins.getElementId(),
                                        this.flow.endSequence._id,
                                        fromStep as FlowStepData,
                                        ins,
                                    ),
                                );
                            }
                        }
                    });
                }
            }
        }

        return conn;
    }

    getErrorFlowConnections(): FlowExtraConnection[] {
        let conn: FlowExtraConnection[] = [];

        // showErrorOrEndConnections is set THROW or error stack is expanded.
        if (
            this.showErrorOrEndConnections === FlowStepOutputInstructionType.THROW_EXCEPTION ||
            !this.flow.errorSequence.isCollapsed ||
            this.isShowErrorBranch
        ) {
            for (let s of this.flow.allSequences) {
                for (let step of [...s.steps, s.endStep]) {
                    step.getOutcomeInstructions().forEach((ins: FlowStepOutputInstructions) => {
                        if (ins.strategy === FlowStepOutputInstructionType.THROW_EXCEPTION) {
                            let fromStep = this.flow.find(ins.stepId);
                            if (fromStep) {
                                conn.push(
                                    new FlowExtraConnection(
                                        ins.getElementId(),
                                        this.flow.errorSequence._id,
                                        fromStep as FlowStepData,
                                        ins,
                                    ),
                                );
                            }
                        }
                    });
                }
            }
        }
        return conn;
    }

    @AutoBind
    setSelectedSequenceForError(elem: HTMLElement, sequence: FlowStepSequence) {
        let elemGeo = HTMLUtilities.getGeometryFromElement(elem, FlowConstants.DESIGN_SURFACE_ID);
        this.selectedSequenceForError = {
            position: {
                x: sequence.x,
                y: (elemGeo?.top || 0) + 15,
            },
            sequence,
        };
    }

    setSelectedForColor(sequence: FlowStepSequence, element: HTMLElement) {
        let pos = HTMLUtilities.getGeometryFromElement(element, FlowConstants.DESIGN_SURFACE_ID);
        this.selectedForColor = {
            instance: sequence,
            position: {
                x: (pos?.left || 0) + 24,
                y: pos?.top || 0,
            },
        };
    }

    isDraggingOverSequence(sequence: FlowStepSequence): boolean {
        let ret = !!(
            this.draggingControl &&
            this.draggingControl.destination &&
            this.draggingControl.destination === sequence._id
        );

        return ret;
    }

    setCandidateDesiredPositionForSeqeuence(sequence: FlowStepSequence) {
        // when sequence is expanded, we need to set the disired position of candidates by instruction.
        if (!sequence.isCollapsed) {
            for (let inst of sequence.instructions) {
                this.setCandidateDesiredPositionForStepStrategy(inst);
            }
        }
        // when sequenece is collapsed, we need to set the disired position of candidates by sequence and index
        else {
            let pos = HTMLUtilities.getGeometry(sequence._id, FlowConstants.DESIGN_SURFACE_ID);
            if (pos) {
                let idx = 0;
                for (let inst of sequence.instructions) {
                    // for hidden step
                    if (inst.stepId.endsWith("_end")) {
                        this.setCandidateDesiredPositionForSequenceStrategy(inst);
                        continue;
                    }

                    let c = this.flow.getCandidateOrRealSequence(inst.connectedSequenceId!);
                    if (c instanceof CandidateSequence) {
                        let insty = pos.top + idx * this.getCandidateCommonStyles().height + 10;
                        let instx = pos.left + pos.width + this.pathCandidateShift;

                        inst.candidateStackY = insty;
                        inst.candidateStackX = instx;
                        idx += 1;
                    }
                }
            }
        }
    }

    getCandidateCommonStyles() {
        return {
            width: this.candidateWidth,
            height: this.candidateHeight,
            padding: this.candidatePadding,
            fontSize: this.candidateFontSize,
            dragging: {
                width: this.candidateDraggingWidth,
                height: this.candidateDraggingHeight,
            },
        };
    }

    setCandidateDesiredPositionForSequenceStrategy(inst: FlowStepOutputInstructions) {
        let seq = this.flow.findSequenceByStepId(inst.stepId);
        if (!seq) return;

        let pos = HTMLUtilities.getGeometry(seq._id, FlowConstants.DESIGN_SURFACE_ID);

        if (pos) {
            // set center below sequence.
            let instx = pos.left + pos.width / 2 - this.getCandidateCommonStyles().width / 2;
            let insty = pos.top + pos.height + this.sequenceCandidateShift;

            inst.candidateStackY = insty;
            inst.candidateStackX = instx < 0 ? 0 : instx;
        }
    }

    setCandidateDesiredPositionForStepStrategy(inst: FlowStepOutputInstructions) {
        // if it's for sequence strategy
        let step = this.flow.find(inst.stepId)!;
        if (step instanceof FlowStepData && FlowUtilities.IsEndStep(step)) {
            this.setCandidateDesiredPositionForSequenceStrategy(inst);
        } else {
            let pos = HTMLUtilities.getGeometry(inst.getElementId(), FlowConstants.DESIGN_SURFACE_ID);

            if (pos) {
                let insty = pos.top + 18 - this.getCandidateCommonStyles().height / 2;
                let instx = pos.left + this.pathCandidateShift;

                inst.candidateStackY = insty;
                inst.candidateStackX = instx;
            }
        }
    }

    getSequenceExtraSize(sequence: FlowStepSequence) {
        return {
            width: 0,
            height:
                sequence.strategy === FlowSequenceCompleteStrategy.LINK ? 0 : FlowConstants.FLOW_CONNECT_AREA_HEIGHT,
        };
    }

    private delayPosition(alwaysRefresh: boolean = false) {
        ReactRenderHook.registerCallback("position", () => {
            if (this.positionCandidateSequences(true)) {
                this.refreshSequences();
            }

            if (alwaysRefresh) this.refreshSequences();
        });
    }

    //@LogTime()s
    private syncCandidates(): boolean {
        let sequenceIds = this.flow.allSequences.map((s: FlowStepSequence) => s._id);

        let wasChanged = false;

        // remove the candidate that has same id with sequence.
        this.flow.candidateSequences = this.flow.candidateSequences.filter((c: IFlowStepSequence) => {
            if (sequenceIds.indexOf(c._id) < 0) {
                let instruction = Reflect.get(c, "instruction") as FlowStepOutputInstructions;

                //                console.log('inst ' + instruction);

                if (instruction) {
                    if (instruction.connectedSequenceId !== c._id) {
                        wasChanged = true;
                        return false;
                    }
                    if (!instruction.stepId) {
                        wasChanged = true;
                        // path declares no step
                        return false;
                    }

                    let step = this.flow.find(instruction.stepId) as FlowStepData;

                    // step still exists
                    if (!step) {
                        wasChanged = true;
                        return false;
                    }

                    // candidate declares no path name
                    if (!instruction.pathName || instruction.pathName === "") {
                        wasChanged = true;
                        return false;
                    }

                    // is outcome the same
                    if (!step.isCurrentOutcome(instruction.pathName)) {
                        wasChanged = true;
                        return false;
                    }

                    // its not set to branch
                    if (instruction.strategy !== FlowStepOutputInstructionType.BRANCH) {
                        wasChanged = true;
                        return false;
                    }

                    return true;
                } else {
                    // this is a valid one
                    return true;
                }
            } else {
                wasChanged = true;
                return false;
            }
        });

        // add missing sequences
        for (let seq of this.flow.allSequences) {
            for (let step of [...seq.steps, seq.endStep]) {
                let paths = [...step.getOutcomeInstructions(), ...seq.instructions];
                if (paths && paths.length > 0) {
                    for (let inst of paths) {
                        if (inst.strategy === FlowStepOutputInstructionType.BRANCH && inst.connectedSequenceId) {
                            if (!this.flow.getCandidateOrRealSequence(inst.connectedSequenceId)) {
                                // its missing, we need to add

                                let cs = new CandidateSequence(inst);
                                cs._id = inst.connectedSequenceId;
                                this.flow.candidateSequences.push(cs);

                                this.flow.invalidateNodes();

                                // mark as changed
                                wasChanged = true;
                            }
                        }
                    }
                }
            }
        }

        return wasChanged;
    }

    @AutoBind
    private cloneFlowElement(s: FlowStepData | FlowStepSequence): FlowStepData | FlowStepSequence {
        let val;
        if (s instanceof FlowStepSequence) {
            val = new FlowStepSequence();

            DataUtilities.spreadDataWithoutFunction(val, DataUtilities.clone(s), [
                "_id",
                "_steps",
                "_hiddenStep",
                "_strategy",
            ]);
            for (let step of s.steps) {
                val.addStep(this.cloneFlowElement(step) as FlowStepData);
            }

            val.strategy = FlowSequenceCompleteStrategy.END_FLOW;
            // copying the sequence strategy.
            // val.strategy = s.strategy;

            // let fromInst = s.hiddenStep.getOutcomeInstructions()[0];
            // let toInst = val.hiddenStep.getOutcomeInstructions()[0]
            // toInst.errorMessage = fromInst.errorMessage;

            // if (s.strategy === FlowStepSequenceStrategy.LINK) {
            //     if (fromInst.connectedSequenceId) {
            //         let s = this.getCandidateOrRealSequence(fromInst.connectedSequenceId);

            //         if (s instanceof FlowStepSequence) {
            //             toInst.connectedSequenceId = s._id;
            //         } else {
            //             toInst.connectedSequenceId = DataUtilities.generateUniqueId();
            //         }
            //     }
            // }
            return val;
        } else if (s instanceof FlowStepData) {
            let registeredData = FlowStepDataRegistration.getByTypeName(s.__type!);

            let data = registeredData.create();

            DataUtilities.spreadDataWithoutFunction(data, DataUtilities.clone(s), [
                "_id",
                "_nonDefaultOutputInstructions",
            ]);

            let paths = s.getOutcomes();
            if (paths) {
                for (let i of paths) {
                    data.findOutputInstruction(i.name);
                }
            }
            return data;
        } else {
            throw new Error("Unknown element type");
        }
    }
}

interface IFlowEditStepStrategy {
    position: {
        x: number;
        y: number;
    };
    step: FlowStepData;
}

interface IFlowEditSequenceDialog {
    position: {
        x: number;
        y: number;
    };
    sequence: FlowStepSequence;
}

function RenderFlowSettingsEditor(props: { flowConfig: FlowConfiguration }) {
    const [showMapping, setShowMapping] = useState<boolean>(false);

    let options = [
        { value: FlowOutputType.NODATA, text: "No Output" },
        { value: FlowOutputType.ADDEDDATA, text: "Added Data" },
        { value: FlowOutputType.ALLDATA, text: "All Data" },
        { value: FlowOutputType.DEFINED, text: "Define Outputs" },
    ];
    return (
        <>
            <ObserveState
                listenTo={props.flowConfig}
                properties={[FlowConfiguration.OUTPUT_STRATEGY]}
                control={() => {
                    return (
                        <>
                            <UILabel>Flow Outputs</UILabel>
                            <UIToggleButtons
                                value={props.flowConfig.defineOutputs}
                                options={options}
                                onChange={(newValue) => {
                                    // @ts-ignore
                                    props.flowConfig.defineOutputs = newValue;
                                }}
                            ></UIToggleButtons>
                            {props.flowConfig.getExecutionResult().fields.length !== 0 && (
                                <UIButton onClick={() => setShowMapping(true)}>Edit Result Mapping</UIButton>
                            )}

                            <FlowMappingLayer
                                flow={props.flowConfig}
                                open={showMapping}
                                onComplete={() => setShowMapping(false)}
                            />
                        </>
                    );
                }}
            />
        </>
    );
}
